import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';
import { useAuth } from '../contexts/AuthContext';
import { STAFF_HOME } from '../config/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth/staff-invite`;
const TOKEN = /^[A-Za-z0-9_-]{20,200}$/;
const inputClass = 'w-full px-4 py-3 border border-zinc-200 rounded-xl bg-white';
const MISSING = 'Davet bağlantısı eksik veya hatalı. E-postadaki ya da sana iletilen bağlantıyı eksiksiz açtığından emin ol.';

// The token lives in the URL fragment (#t=...), so browsers never send it to the server in the address.
const readToken = () => new URLSearchParams(window.location.hash.slice(1)).get('t') || '';

const errorText = error => {
  if (['ECONNABORTED', 'ETIMEDOUT'].includes(error?.code)) return 'Sunucu zamanında yanıt vermedi. Biraz sonra tekrar dene.';
  if (!error?.response) return 'Sunucuya ulaşılamadı. İnternet bağlantını kontrol edip tekrar dene.';
  const { status, data } = error.response;
  if (typeof data?.detail === 'string') return data.detail;
  if (status === 429) return 'Çok fazla deneme yapıldı. Lütfen 10 dakika sonra tekrar dene.';
  if (status === 410) return 'Bu davet artık geçerli değil. Yeni davet için ana yöneticiye yaz.';
  if (status === 404) return 'Davet bağlantısı geçersiz.';
  if (status === 422) return 'Bilgileri kontrol et: ad en az 2, şifre en az 8 karakter olmalı.';
  return 'İşlem tamamlanamadı. Lütfen tekrar dene.';
};

export default function StaffInviteRegister() {
  const navigate = useNavigate();
  const { acceptSession } = useAuth();
  const [token] = useState(readToken);
  const valid = TOKEN.test(token);
  const [invite, setInvite] = useState(null);
  const [status, setStatus] = useState(valid ? 'loading' : 'error');
  const [error, setError] = useState(valid ? '' : MISSING);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);

  useEffect(() => {
    // Remove the one-time token from the address bar and history right away.
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
    if (!valid) return undefined;
    let alive = true;
    axios.post(`${API}/check`, { token }, { timeout: 15000 })
      .then(({ data }) => { if (!alive) return; setInvite(data); setName(data?.name || ''); setStatus('ready'); })
      .catch(err => { if (!alive) return; setError(errorText(err)); setStatus('error'); });
    return () => { alive = false; };
  }, [token, valid]);

  const submit = async event => {
    event.preventDefault();
    if (submitting.current) return;
    setError('');
    if (name.trim().length < 2) { setError('Adını ve soyadını yaz.'); return; }
    if (password.length < 8 || new TextEncoder().encode(password).length > 72) {
      setError('Şifre en az 8 karakter olmalı ve çok uzun olmamalı.'); return;
    }
    if (password !== repeat) { setError('Şifreler eşleşmiyor.'); return; }
    submitting.current = true; setBusy(true);
    try {
      const { data } = await axios.post(`${API}/redeem`, { token, name: name.trim(), password }, { timeout: 20000 });
      acceptSession(data);
      navigate(STAFF_HOME, { replace: true });
    } catch (err) {
      setError(errorText(err));
      if (err?.response?.status === 410) setStatus('error');
    } finally { submitting.current = false; setBusy(false); }
  };

  return <main className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6 py-10">
    <div className="w-full max-w-md" data-testid="staff-invite-page">
      <div className="mb-6 flex justify-center"><Logo size="xl" variant="mark" /></div>
      <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
        <h1 className="text-2xl font-semibold mb-2">Personel hesabını aç</h1>
        {status === 'loading' && <p role="status">Davet kontrol ediliyor…</p>}
        {status === 'error' && <>
          <p role="alert" className="text-sm" style={{ color: 'var(--danger-text, #b42318)' }}>{error}</p>
          <Link to="/login" className="inline-block mt-6 text-sm underline">Giriş sayfasına dön</Link>
        </>}
        {status === 'ready' && <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-sm text-zinc-500">MekandaPro ekibine Havuz Yöneticisi olarak davet edildin. Hesabın yalnız personel işleri içindir.</p>
          <div><span className="block text-sm text-zinc-500 mb-1">E-posta</span>
            <strong data-testid="staff-invite-email" className="break-all">{invite?.email}</strong></div>
          <label className="block text-sm">Ad soyad
            <input className={`${inputClass} mt-1`} value={name} onChange={e => setName(e.target.value)} maxLength={120}
              autoComplete="name" required data-testid="staff-invite-name" /></label>
          <label className="block text-sm">Şifre
            <input className={`${inputClass} mt-1`} type="password" value={password} onChange={e => setPassword(e.target.value)}
              autoComplete="new-password" minLength={8} required data-testid="staff-invite-password" /></label>
          <label className="block text-sm">Şifre (tekrar)
            <input className={`${inputClass} mt-1`} type="password" value={repeat} onChange={e => setRepeat(e.target.value)}
              autoComplete="new-password" minLength={8} required data-testid="staff-invite-password-repeat" /></label>
          {error && <p role="alert" className="text-sm" style={{ color: 'var(--danger-text, #b42318)' }}>{error}</p>}
          <button type="submit" disabled={busy} className="action-primary w-full" data-testid="staff-invite-submit">
            {busy ? 'Hesap açılıyor…' : 'Hesabımı aç'}</button>
          <p className="text-xs text-zinc-500">Davet bağlantısı tek kullanımlıktır ve 7 gün geçerlidir.</p>
        </form>}
      </div>
    </div>
  </main>;
}
